package gr.tech.meetinggenie.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaCircleInfo
import com.varabyte.kobweb.silk.components.icons.fa.FaEye
import com.varabyte.kobweb.silk.components.icons.fa.FaEyeSlash
import com.varabyte.kobweb.silk.components.icons.fa.FaInfo
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.overlay.KeepPopupOpenStrategy
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.meetinggenie.models.meeting.ActiveProjectDTO
import gr.tech.meetinggenie.models.meeting.ClosingSoonDTO
import gr.tech.meetinggenie.models.meeting.LossDTO
import gr.tech.meetinggenie.models.meeting.MeetingFormDataDTO
import gr.tech.meetinggenie.models.meeting.MeetingResponseDTO
import gr.tech.meetinggenie.models.meeting.ProjectUpdateDTO
import gr.tech.meetinggenie.models.meeting.PulseCheckDTO
import gr.tech.meetinggenie.models.meeting.WeeklySummaryDTO
import gr.tech.meetinggenie.models.meeting.WinDTO
import gr.tech.meetinggenie.styles.ButtonStyle
import gr.tech.meetinggenie.styles.ModalCardStyle
import gr.tech.meetinggenie.styles.ResponseCardStyle
import gr.tech.meetinggenie.styles.ResponseStatusBadgeStyle
import gr.tech.meetinggenie.styles.SectionHeadingStyle
import gr.tech.meetinggenie.styles.SubSectionHeadingStyle
import gr.tech.meetinggenie.styles.TableCellBoldTextStyle
import gr.tech.meetinggenie.styles.TableCellTextStyle
import gr.tech.meetinggenie.styles.Theme
import gr.tech.meetinggenie.styles.ToolTipStyle
import gr.tech.meetinggenie.styles.ToolTipTextStyle
import gr.tech.meetinggenie.styles.TooltipIconStyle
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Section 1 – View Responses.
 * Priority personnel are shown as cards at the top.
 * Guests (is_priority == false) are in a collapsible section at the bottom.
 */
@Composable
fun ResponsesView(responses: List<MeetingResponseDTO>) {
    val priority = responses.filter { it.isPriority }
    val guests   = responses.filter { !it.isPriority }

    Column(
        modifier = Modifier.fillMaxWidth().padding(24.px),
        horizontalAlignment = Alignment.Start
    ) {
        H2(SectionHeadingStyle.toModifier().toAttrs()) {
            Text("Responses (${priority.size})")
        }

        if (responses.isEmpty()) {
            SpanText("No responses yet.", modifier = TableCellTextStyle.toModifier().color(Colors.Gray))
            return@Column
        }

        priority.forEach { response -> ResponseCard(response) }

        if (guests.isNotEmpty()) {
            Div(Modifier.fillMaxWidth().margin(top = 16.px).toAttrs()) {
                H3(SubSectionHeadingStyle.toModifier().toAttrs()) {
                    Text("Guests (${guests.size})")
                    FaCircleInfo(
                        modifier = TooltipIconStyle.toModifier(),
                        size = IconSize.XXS
                    )
                    Tooltip(
                        target = ElementTarget.PreviousSibling,
                        modifier = ToolTipStyle.toModifier(),
                        placement = PopupPlacement.Right,
                        hasArrow = false,
                        showDelayMs = 100,
                        hideDelayMs = 100,
                        content = {
                            P(attrs = ToolTipTextStyle.toAttrs()) { Text("Guests are individuals who don’t typically participate in Green Rebates meetings but are included in this one.") }
                        }
                    )

                }
                guests.forEach { response -> ResponseCard(response) }
            }
        }
    }
}

@Composable
private fun ResponseCard(response: MeetingResponseDTO) {

    var viewUserToggle by remember { mutableStateOf(false) }

    Div(attrs = ResponseCardStyle.toModifier().toAttrs()) {
        Row(
            modifier = Modifier
                .margin(bottom = if (viewUserToggle) 12.px else 0.px),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Button(
                attrs = ButtonStyle.toModifier().margin(right = 4.px).padding(6.px)
                    .backgroundColor(if (viewUserToggle) Theme.MediumGreen.rgb else Colors.LightGray)
                    .onClick { viewUserToggle = !viewUserToggle }.toAttrs()
            ) {
                if (viewUserToggle) FaEye() else FaEyeSlash()
            }

            H3(SectionHeadingStyle.toModifier().margin(0.px).toAttrs()) {
                Text(response.userName ?: "Error")
            }

            if (response.submitted) {
                Span(ResponseStatusBadgeStyle.toModifier().color(Colors.Green).toAttrs()) { Text("● Submitted") }
            } else {
                Span(ResponseStatusBadgeStyle.toModifier().color(Colors.DarkRed).toAttrs()) { Text("○ Not Submitted") }
            }
        }

        val data = response.formData

        // Only render sections that have content
        val hasWins           = data.wins.any { it.dealName.isNotBlank() }
        val hasLosses         = data.losses.any { it.accountName.isNotBlank() }
        val hasActiveProjects = data.activeProjects.any { it.projectName.isNotBlank() }
        val hasClosingSoon    = data.closingSoon.any { it.accountName.isNotBlank() }
        val hasProjectUpdates = data.projectUpdates.any { it.accountName.isNotBlank() }
        val hasSummary        = data.weeklySummary.oneSentenceSummary.isNotBlank() || data.weeklySummary.topPriorityNextWeek.isNotBlank()
        val hasPulse          = data.pulseCheck.weeklyFeel != null || data.pulseCheck.blockersNote.isNotBlank()

        if (viewUserToggle) {

            if (!hasWins && !hasLosses && !hasActiveProjects && !hasClosingSoon && !hasProjectUpdates && !hasSummary && !hasPulse) {
                SpanText("No data submitted yet.", modifier = TableCellTextStyle.toModifier().color(Colors.Gray))
                return@Div
            }

            if (hasWins) {
                ResponseSectionHeading("Wins This Week")
                WinsTable(data.wins.filter { it.dealName.isNotBlank() })
                HorizontalDivider(Modifier.margin(topBottom = 12.px))
            }

            if (hasLosses) {
                ResponseSectionHeading("Losses / Delayed Deals")
                LossesTable(data.losses.filter { it.accountName.isNotBlank() })
                HorizontalDivider(Modifier.margin(topBottom = 12.px))
            }

            if (hasActiveProjects) {
                ResponseSectionHeading("Active Projects")
                ActiveProjectsTable(data.activeProjects.filter { it.projectName.isNotBlank() })
                HorizontalDivider(Modifier.margin(topBottom = 12.px))
            }

            if (hasClosingSoon) {
                ResponseSectionHeading("Projects Hoping to Close Soon")
                ClosingSoonTable(data.closingSoon.filter { it.accountName.isNotBlank() })
                HorizontalDivider(Modifier.margin(topBottom = 12.px))
            }

            if (hasProjectUpdates) {
                ResponseSectionHeading("Project Updates")
                ProjectUpdatesTable(data.projectUpdates.filter { it.accountName.isNotBlank() })
                HorizontalDivider(Modifier.margin(topBottom = 12.px))
            }

            if (hasSummary) {
                ResponseSectionHeading("Weekly Summary")
                WeeklySummaryView(data.weeklySummary)
                HorizontalDivider(Modifier.margin(topBottom = 12.px))
            }

            if (hasPulse) {
                ResponseSectionHeading("Pulse Check")
                PulseCheckView(data.pulseCheck)
            }
        }
    }
}

@Composable
private fun ResponseSectionHeading(title: String) {
    H4(
        attrs = Modifier
            .margin(top = 0.px, bottom = 6.px)
            .fontSize(0.9.cssRem)
            .fontWeight(700)
            .color(Colors.DarkSlateGray)
            .toAttrs()
    ) { Text(title) }
}

// ──────────────────────────────────────────────────────────────────
// Wins
// ──────────────────────────────────────────────────────────────────

@Composable
private fun WinsTable(wins: List<WinDTO>) {
    ResponseTable(
        headers = listOf("Deal", "Size / Impact", "Why It Matters", "🔥"),
        columnWidths = listOf("25%", "20%", "40%", "15%")
    ) {
        wins.forEach { win ->
            Tr {
                ResponseTd { Text(win.dealName) }
                ResponseTd { Text(win.dealSizeOrImpact) }
                ResponseTd { Text(win.whyThisWinMatters) }
                ResponseTd { Text("🔥".repeat(win.winSize.coerceIn(1, 3))) }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Losses
// ──────────────────────────────────────────────────────────────────

@Composable
private fun LossesTable(losses: List<LossDTO>) {
    ResponseTable(
        headers = listOf("Account", "Status", "Primary Reason", "Lesson / Signal"),
        columnWidths = listOf("20%", "15%", "20%", "45%")
    ) {
        losses.forEach { loss ->
            Tr {
                ResponseTd { Text(loss.accountName) }
                ResponseTd { Text(loss.dealStatus?.label ?: "—") }
                ResponseTd { Text(loss.primaryReason?.label ?: "—") }
                ResponseTd { Text(loss.lessonOrSignal.ifBlank { "—" }) }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Active Projects
// ──────────────────────────────────────────────────────────────────

@Composable
private fun ActiveProjectsTable(projects: List<ActiveProjectDTO>) {
    ResponseTable(
        headers = listOf("Project", "Stage", "Biggest Blocker", "Needs"),
        columnWidths = listOf("20%", "15%", "30%", "35%")
    ) {
        projects.forEach { project ->
            Tr {
                ResponseTd { Text(project.projectName) }
                ResponseTd { Text(project.dealStage?.label ?: "—") }
                ResponseTd { Text(project.biggestBlocker.ifBlank { "—" }) }
                ResponseTd {
                    if (project.needs.isEmpty()) {
                        Text("—")
                    } else {
                        Text(project.needs.joinToString(", ") { it.label })
                    }
                }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Closing Soon
// ──────────────────────────────────────────────────────────────────

@Composable
private fun ClosingSoonTable(items: List<ClosingSoonDTO>) {
    ResponseTable(
        headers = listOf("Account", "Expected Close", "Confidence", "Next Steps"),
        columnWidths = listOf("20%", "15%", "15%", "50%")
    ) {
        items.forEach { item ->
            Tr {
                ResponseTd { Text(item.accountName) }
                ResponseTd { Text(item.expectedCloseDate.ifBlank { "—" }) }
                ResponseTd { Text(item.confidenceLevel?.label ?: "—") }
                ResponseTd { Text(item.whatHappensNext.ifBlank { "—" }) }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Project Updates
// ──────────────────────────────────────────────────────────────────

@Composable
private fun ProjectUpdatesTable(updates: List<ProjectUpdateDTO>) {
    ResponseTable(
        headers = listOf("Account", "What Changed"),
        columnWidths = listOf("25%", "75%")
    ) {
        updates.forEach { update ->
            Tr {
                ResponseTd { Text(update.accountName) }
                ResponseTd { Text(update.whatChanged.ifBlank { "—" }) }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Weekly Summary (singleton)
// ──────────────────────────────────────────────────────────────────

@Composable
private fun WeeklySummaryView(summary: WeeklySummaryDTO) {
    Column(modifier = Modifier.gap(6.px).fillMaxWidth()) {
        if (summary.oneSentenceSummary.isNotBlank()) {
            ResponseFieldRow(label = "Summary", value = summary.oneSentenceSummary)
        }
        if (summary.topPriorityNextWeek.isNotBlank()) {
            ResponseFieldRow(label = "Top Priority Next Week", value = summary.topPriorityNextWeek)
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Pulse Check (singleton)
// ──────────────────────────────────────────────────────────────────

@Composable
private fun PulseCheckView(pulseCheck: PulseCheckDTO) {
    Column(modifier = Modifier.gap(6.px).fillMaxWidth()) {
        if (pulseCheck.weeklyFeel != null) {
            ResponseFieldRow(label = "How's the week?", value = pulseCheck.weeklyFeel!!.label)
        }
        if (pulseCheck.blockersNote.isNotBlank()) {
            ResponseFieldRow(label = "Blockers / Notes", value = pulseCheck.blockersNote)
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Shared helpers
// ──────────────────────────────────────────────────────────────────

@Composable
private fun ResponseFieldRow(label: String, value: String) {
    Row(modifier = Modifier.fillMaxWidth().gap(8.px)) {
        SpanText(
            "$label:",
            modifier = TableCellBoldTextStyle.toModifier()
                .minWidth(160.px)
                .color(Colors.DarkSlateGray)
        )
        SpanText(value, modifier = TableCellTextStyle.toModifier())
    }
}

@Composable
private fun ResponseTable(
    headers: List<String>,
    columnWidths: List<String>,
    content: @Composable () -> Unit
) {
    Table(
        attrs = Modifier
            .fillMaxWidth()
            .margin(bottom = 8.px)
            .fontSize(0.8.cssRem)
            .toAttrs {
                style {
                    property("border-collapse", "collapse")
                    property("table-layout", "fixed")
                }
            }
    ) {
        Thead {
            Tr {
                headers.forEachIndexed { i, header ->
                    Th(
                        attrs = Modifier
                            .padding(leftRight = 8.px, topBottom = 4.px)
                            .backgroundColor(Colors.WhiteSmoke)
                            .color(Colors.DimGray)
                            .fontWeight(600)
                            .fontSize(0.75.cssRem)
                            .toAttrs {
                                style {
                                    property("text-align", "left")
                                    property("border-bottom", "1px solid #ddd")
                                    if (i < columnWidths.size) property("width", columnWidths[i])
                                }
                            }
                    ) { Text(header) }
                }
            }
        }
        Tbody { content() }
    }
}

@Composable
private fun ResponseTd(content: @Composable () -> Unit) {
    Td(
        attrs = Modifier
            .padding(leftRight = 8.px, topBottom = 6.px)
            .color(Colors.DarkSlateGray)
            .toAttrs {
                style {
                    property("border-bottom", "1px solid #f0f0f0")
                    property("word-break", "break-word")
                    property("vertical-align", "top")
                }
            }
    ) { content() }
}
